import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { get } from 'lodash'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import CardContent from '@material-ui/core/CardContent'
import CardActions from '@material-ui/core/CardActions'
import Box from '@material-ui/core/Box'
import Chip from '@material-ui/core/Chip'
import Typography from '@material-ui/core/Typography'
import Tooltip from '@material-ui/core/Tooltip'
import ShareOutlinedIcon from '@material-ui/icons/ShareOutlined'
import EditOutlinedIcon from '@material-ui/icons/EditOutlined'
import LockOutlinedIcon from '@material-ui/icons/LockOutlined'
import { makeStyles } from '@material-ui/core/styles'
import IconButton from '@material-ui/core/IconButton'
import CollectionsBookmarkOutlinedIcon from '@material-ui/icons/CollectionsBookmarkOutlined'
import { MY_PGA_COM_HOST } from 'env'
import useIconAccessor from 'components/lesson-type-category/icons'
import SocialShareDialog from 'components/social-share-dialog'
import pluralize from 'lib/pluralize'
import clsx from 'clsx'
import { Avatar } from '@material-ui/core'
import LessonPackDetailsDialog from 'components/lesson-pack-details-dialog'
import { toFullTextDayDateWithTimezone } from 'utils/dateUtils'
import { durationText } from 'utils/durationUtils'
import RedeemIcon from '@mui/icons-material/Redeem'
import VisibilityOffOutlinedIcon from '@material-ui/icons/VisibilityOffOutlined'
import { getPaymentAccountWarningChip } from '../events/utils'

const useStyles = makeStyles(theme => ({
  headerContent: {
    display: 'flex',
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    gap: '8px',
    alignContent: 'flex-start',
  },
  headerRoot: {
    paddingBottom: '24px',
  },
  actionContent: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: '12px',
  },
  avatar: {
    color: 'rgba(0, 35, 75, 1)',
    backgroundColor: 'rgba(244, 246, 248, 1)',
    width: '56px',
    height: '56px',
    marginRight: '4px',
  },
  text: {
    color: 'rgba(0, 0, 0, 0.54)',
    marginRight: '4px',
  },
  button: {
    paddingLeft: 0,
  },
  chip: {
    color: '#fff',
    borderRadius: '8px',
    height: '22px',
    fontSize: '10px',
    background: theme.palette.primary.main,
  },
  chipInfo: {
    background: theme.palette.blue.main,
  },
  chipAttention: {
    backgroundColor: '#feecea',
    color: '#000',
  },
  chipPrivate: {
    backgroundColor: '#ebeef1',
    color: '#000',
  },
  chipGiftable: {
    background: '#fff',
    color: theme.palette.blue.main,
  },
  redeemIcon: {
    color: theme.palette.blue.main,
    marginRight: '4px',
    marginTop: '-2px',
    width: '16px !important',
    height: '16px !important',
  },
  chipGiftableText: {
    color: theme.palette.blue.main,
    fontSize: '10px',
    display: 'flex',
    alignItems: 'center',
  },
  cardTitle: ({ isLessonPack }) => ({
    marginBottom: '8px',
    display: '-webkit-box',
    lineClamp: isLessonPack ? 1 : 2,
    msLineClamp: isLessonPack ? 1 : 2, // Edge/IE support
    WebkitBoxOrient: 'vertical',
    msBoxOrient: 'vertical', // Edge/IE support
    overflow: 'clip',
    textOverflow: 'ellipsis',
    lineHeight: '1.2em',
  }),
  errorIcon: {
    color: '#000000DE',
  },
  unableToAcceptPaymentsChip: {
    background: '#F88078',
    fontSize: '10px',
    height: '22px',
  },
  payoutsOnHoldChip: {
    background: '#FFB547',
    fontSize: '10px',
    height: '22px',
  },
}))

const formatBookingTypeUrl = lessonType => {
  if (!lessonType) {
    return null
  }

  const coachId = lessonType.coach.externalId
  const coachSlug = get(lessonType, 'coach.coachProfile.slug')
  const lessonTypeId = lessonType.id
  const locationId = get(lessonType, 'locations[0].id')

  return `${MY_PGA_COM_HOST}/coach/${coachSlug ||
    coachId}/schedule?lessonTypeId=${lessonTypeId}&locationId=${locationId}`
}

const composeTitle = (bt, isLessonPack, classes) => {
  const paymentAccountWarningChip = getPaymentAccountWarningChip({
    event: bt,
    classes,
    isMobile: true,
  })

  return (
    <>
      {bt.lessonPacks.length > 0 ? (
        <Chip
          avatar={
            <CollectionsBookmarkOutlinedIcon
              style={{ color: '#fff', width: '16px', height: '16px' }}
            />
          }
          label="Lesson Pack"
          className={clsx(classes.chip, classes.chipInfo)}
        />
      ) : null}
      {bt.giftingEnabled && (
        <Box
          display="flex"
          alignItems="center"
          mr={1}
          className={classes.chipGiftable}
        >
          <RedeemIcon className={classes.redeemIcon} />
          <Typography variant="caption" className={classes.chipGiftableText}>
            Giftable
          </Typography>
        </Box>
      )}
      {isLessonPack && bt.remainingCredits > 0 && (
        <Chip
          label={`${bt.remainingCredits}/${bt.totalCredits} Remaining`}
          className={clsx(classes.chip, classes.chipInfo)}
        />
      )}
      {bt.status === 'INACTIVE' && (
        <Chip
          avatar={
            <VisibilityOffOutlinedIcon
              style={{ width: '16px', height: '16px', color: '#000' }}
            />
          }
          label="Private"
          className={clsx(classes.chip, classes.chipPrivate)}
        />
      )}
      {bt.accessCode && (
        <Chip
          avatar={
            <LockOutlinedIcon
              style={{ width: '16px', height: '16px', color: '#000' }}
            />
          }
          label="Access Code"
          className={clsx(classes.chip, classes.chipAttention)}
        />
      )}
      {paymentAccountWarningChip}
    </>
  )
}

const BookingTypeCard = ({ bookingType, isLessonPack = false, refetch }) => {
  const classes = useStyles({ isLessonPack })
  const history = useHistory()
  const [isOpen, setIsOpen] = useState(false)
  const bookingTypeUrl = formatBookingTypeUrl(bookingType)
  const lessonPacksLength = bookingType.lessonPacks.length
  const accessor = useIconAccessor()
  const isExpired =
    bookingType.expiresAt && Date.parse(bookingType.expiresAt) < Date.now()

  return (
    <Card variant="outlined">
      <CardHeader
        disableTypography
        title={composeTitle(bookingType, isLessonPack, classes)}
        avatar={
          <Avatar variant="circular" className={classes.avatar}>
            {accessor[bookingType.category].icon}
          </Avatar>
        }
        titleTypographyProps={{ variant: 'subtitle1' }}
        classes={{
          content: classes.headerContent,
          root: classes.headerRoot,
        }}
      />
      <CardContent>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          style={{ height: '20px' }}
        >
          <Typography variant="h6" className={classes.cardTitle}>
            {bookingType.title}
          </Typography>

          {lessonPacksLength === 0 ? (
            <>
              <Box display="flex">
                <Typography variant="caption" className={classes.text}>
                  {bookingType.locations[0].name}
                </Typography>
              </Box>
              <Box display="flex">
                <Typography variant="caption" className={classes.text}>
                  {durationText(bookingType.durationInMinutes)}
                </Typography>
              </Box>
              {isLessonPack && (
                <Box display="flex">
                  <Typography variant="caption" className={classes.text}>
                    {bookingType.expiresAt
                      ? `Expires ${toFullTextDayDateWithTimezone(
                        bookingType.expiresAt,
                      )}`
                      : 'Does not expire'}
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <Box display="flex" flexDirection="column">
              <Typography
                gutterBottom
                variant="caption"
                className={classes.text}
              >
                {bookingType.locations[0].name}
              </Typography>
              <Typography variant="caption" className={classes.text}>
                {bookingType.lessonPacks.map((lessonPack, i) => (
                  <>
                    {lessonPacksLength === i + 1 && lessonPacksLength !== 1
                      ? ' and '
                      : null}
                    {i !== 0 && lessonPacksLength !== i + 1 ? ', ' : null}
                    {lessonPack.quantity}
                  </>
                ))}
                {pluralize(lessonPacksLength, ' pack')};
                <span>
                  {` ${durationText(bookingType.durationInMinutes)} `}/ lesson
                </span>
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
      <CardActions
        className={classes.actions}
        classes={{
          root: classes.actionContent,
        }}
      >
        <Tooltip
          title={
            isExpired ? 'Lesson Pack has expired and cannot be edited' : ''
          }
        >
          <IconButton
            data-cy={bookingType.title + ' edit'}
            onClick={() => {
              if (isExpired) return
              return isLessonPack
                ? setIsOpen(!isOpen)
                : history.push(
                  `/pga-coach/bookings/booking-types/edit/${bookingType.id}`,
                )
            }}
          >
            <EditOutlinedIcon color={isExpired ? 'disabled' : 'primary'} />
          </IconButton>
        </Tooltip>
        <LessonPackDetailsDialog
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          booking={bookingType}
          refetch={refetch}
        />
        {bookingTypeUrl && (
          <SocialShareDialog
            trigger={
              <IconButton>
                <ShareOutlinedIcon color="primary" />
              </IconButton>
            }
            dialogTitle="Share"
            shareTitle={bookingType.title}
            shareUrl={bookingTypeUrl}
            bookingType={bookingType}
            att="pga-lesson-type"
          />
        )}
      </CardActions>
    </Card>
  )
}

export default BookingTypeCard
